/**
 * Grid scroll restore
 * -----------------------------------------------------------------------------
 * When a shopper opens a product from a search/collection grid and then presses
 * back, return them to the product they tapped instead of the top of the grid.
 *
 * Browsers try to restore scroll on back navigation themselves, but on phones it
 * is unreliable (the page reloads because Shopify sends no-store, and late
 * content can make the restore land at the top). We remember the tapped card and
 * its on-screen position, and correct the scroll on return only if needed.
 */
;(() => {
  const STORAGE_KEY = 'hh:grid-scroll'
  const MAX_AGE_MS = 30 * 60 * 1000
  const CARD_SELECTOR = '#AjaxContent [data-product-grid-item]'

  let storage
  try {
    storage = window.sessionStorage
  } catch (e) {
    return
  }
  if (!storage) return

  const readSaved = () => {
    try {
      return JSON.parse(storage.getItem(STORAGE_KEY))
    } catch (e) {
      return null
    }
  }

  const isUsable = (saved) =>
    saved && saved.url === window.location.href && Date.now() - saved.time <= MAX_AGE_MS

  let clickedCard = null
  // True while this page is a back/forward-cache copy the shopper hasn't touched yet
  let untouchedCacheCopy = false

  document.addEventListener(
    'click',
    (evt) => {
      const link = evt.target.closest && evt.target.closest('a[href]')
      clickedCard = (link && link.closest(CARD_SELECTOR)) || null
    },
    true
  )

  ;['wheel', 'touchstart', 'keydown'].forEach((type) =>
    window.addEventListener(
      type,
      () => {
        untouchedCacheCopy = false
      },
      { passive: true }
    )
  )

  // Save on pagehide so the position is the one the shopper left from
  window.addEventListener('pagehide', () => {
    // iOS Safari shows the cached copy scrolled to the top, then throws it away and reloads it
    // about a second later. Saving here would replace the tapped product with the top of the page.
    if (untouchedCacheCopy && !clickedCard) return

    const card = clickedCard && clickedCard.isConnected ? clickedCard : null
    try {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          url: window.location.href,
          y: window.scrollY,
          handle: card ? card.dataset.productHandle : null,
          cardTop: card ? card.getBoundingClientRect().top : null,
          time: Date.now()
        })
      )
    } catch (e) {}

    // If the page comes back from the back/forward cache, the next exit is a new one: don't
    // carry this card over to it
    clickedCard = null
  })

  const startRestore = (saved) => {
    // Once we have put the shopper back, any scroll of theirs wins over later corrections. A touch
    // before that (e.g. while the reloading page still shows the top) must not cancel the restore.
    let restored = false
    let userScrolled = false
    const stopOnUserScroll = () => {
      userScrolled = true
    }

    const targetY = () => {
      if (saved.handle && saved.cardTop !== null) {
        const card = document.querySelector(`${CARD_SELECTOR}[data-product-handle="${CSS.escape(saved.handle)}"]`)
        if (card) return window.scrollY + card.getBoundingClientRect().top - saved.cardTop
      }
      return saved.y
    }

    const restore = () => {
      if (userScrolled) return
      const y = targetY()
      if (Math.abs(window.scrollY - y) > 50) window.scrollTo({ top: y, behavior: 'instant' })

      if (!restored) {
        restored = true
        ;['wheel', 'touchstart', 'keydown'].forEach((type) =>
          window.addEventListener(type, stopOnUserScroll, { once: true, passive: true })
        )
      }
    }

    // Re-check as late content (images, hydrated components) settles
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', restore, { once: true })
    } else {
      restore()
    }
    if (document.readyState === 'complete') {
      setTimeout(restore, 300)
      setTimeout(restore, 1000)
    } else {
      window.addEventListener('load', () => {
        restore()
        setTimeout(restore, 300)
        setTimeout(restore, 1000)
      })
    }
  }

  // Back/forward-cache copy: the page's state is kept, but iOS Safari shows it scrolled to the top
  window.addEventListener('pageshow', (evt) => {
    if (!evt.persisted) return
    untouchedCacheCopy = true
    const saved = readSaved()
    if (isUsable(saved) && saved.handle) startRestore(saved)
  })

  // The navigation type can't be trusted here: on a real Android phone, going back to this
  // (no-store) page was reported as 'reload' on one visit and 'navigate' on another. So restore
  // when the shopper left this exact URL by tapping a product card, or on a back/reload.
  const navEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0]
  const isReturnVisit = navEntry
    ? navEntry.type === 'back_forward' || navEntry.type === 'reload'
    : performance.navigation && (performance.navigation.type === 1 || performance.navigation.type === 2)

  const saved = readSaved()
  // One-shot: leaving the page again (pagehide) saves a fresh entry
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (e) {}
  if (!isUsable(saved)) return
  if (!saved.handle && !isReturnVisit) return

  startRestore(saved)
})()
